import { ReportSdk } from "lib-klab-sdks";

type ISODateTime = string;
type DateRange = { startDate: string; endDate?: string };

export const connectivityReportsSdkMock = {
    getConnectivityReport: (
        employeeId = 123,
        dateRange: DateRange = buildDefaultLast30DaysRange(),
    ): ReportSdk.ConnectivityResponse => {
        const normalizedRange = normalizeRange(dateRange);

        const content = generateConnectivityItemsForRange(employeeId, normalizedRange);

        return {
            metadata: {
                employeeId,
                dateRange: normalizedRange,
            },
            content,
        } as ReportSdk.ConnectivityResponse;
    },
};

function normalizeRange(range: DateRange): Required<DateRange> {
    const start = parseYmd(range.startDate);
    const end = parseYmd(range.endDate ?? range.startDate);

    const s = start.getTime();
    const e = end.getTime();

    if (s <= e) return { startDate: toYmd(start), endDate: toYmd(end) };
    return { startDate: toYmd(end), endDate: toYmd(start) };
}

function generateConnectivityItemsForRange(
    employeeId: number,
    dateRange: Required<DateRange>,
): ReportSdk.ConnectivityItem[] {
    const start = parseYmd(dateRange.startDate);
    const end = parseYmd(dateRange.endDate);

    const days = enumerateDaysInclusive(start, end);

    const result: ReportSdk.ConnectivityItem[] = [];

    for (let i = 0; i < days.length; i++) {
        const day = days[i];
        const date = toYmd(day);

        const totalSeconds = clamp(
            60 * 60 * 8 + jitter(60 * 20, i, 1),
            60 * 60 * 6,
            60 * 60 * 11,
        );

        const officeSeconds = clamp(
            totalSeconds - (60 * 10 + jitter(60 * 8, i, 2)),
            0,
            totalSeconds,
        );

        const activeSeconds = clamp(
            officeSeconds - (60 * 20 + jitter(60 * 12, i, 3)),
            0,
            officeSeconds,
        );

        const productiveSeconds = clamp(
            Math.floor(activeSeconds * (0.55 + ((i % 7) * 0.03))),
            0,
            activeSeconds,
        );

        const unproductiveSeconds = clamp(
            Math.floor(activeSeconds * (0.05 + ((i % 5) * 0.01))),
            0,
            activeSeconds - productiveSeconds,
        );

        const neutralSeconds = clamp(
            Math.floor(activeSeconds * (0.06 + ((i % 6) * 0.01))),
            0,
            activeSeconds - productiveSeconds - unproductiveSeconds,
        );

        const bestTimeDay = pickBestTime(i);

        const productivityPercentage =
            activeSeconds > 0 ? (productiveSeconds / activeSeconds) * 100 : 0;

        const startedAt = withTime(day, 9, (i * 7) % 50, 0);
        const endedAt = addSeconds(startedAt, totalSeconds);

        result.push({
            employeeId,
            date,
            startedAt: toIsoZ(startedAt) as ISODateTime,
            endedAt: toIsoZ(endedAt) as ISODateTime,

            totalSeconds,
            officeSeconds,
            activeSeconds,

            productiveSeconds,
            unproductiveSeconds,
            neutralSeconds,

            totalWorkDaySeconds: totalSeconds,
            productivityPercentage,
            bestTimeDay,
        } as any);
    }

    result.sort((a, b) => String(b.date).localeCompare(String(a.date)));

    return result;
}

function pickBestTime(i: number): string {
    const hh = [10, 11, 14, 15, 16][i % 5];
    const mm = [0, 15, 30, 45][i % 4];
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
}

function buildDefaultLast30DaysRange(): Required<DateRange> {
    const end = startOfToday();
    const start = new Date(end);
    start.setDate(start.getDate() - 29);
    return { startDate: toYmd(start), endDate: toYmd(end) };
}

function startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function parseYmd(ymd: string): Date {
    const [y, m, d] = String(ymd).split("-").map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
}

function enumerateDaysInclusive(start: Date, end: Date): Date[] {
    const res: Date[] = [];
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);

    const last = new Date(end);
    last.setHours(0, 0, 0, 0);

    while (cur.getTime() <= last.getTime()) {
        res.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return res;
}

function withTime(day: Date, hh: number, mm: number, ss: number): Date {
    const d = new Date(day);
    d.setHours(hh, mm, ss, 0);
    return d;
}

function addSeconds(date: Date, seconds: number): Date {
    return new Date(date.getTime() + seconds * 1000);
}

function toIsoZ(d: Date): string {
    return new Date(d.getTime()).toISOString();
}

function jitter(baseSeconds: number, a: number, b: number): number {
    const delta = ((a * 17 + b * 31) % 9) * 60;
    return Math.floor(baseSeconds + delta);
}

function clamp(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, n));
}

function toYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}